import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { changeManagement } from '../data/mock';
import { Target, Users, GraduationCap, BarChart3, CheckCircle2, Sparkles, ArrowRight, Quote } from 'lucide-react';

const iconMap = {
  Target,
  Users,
  GraduationCap,
  BarChart3,
};

// Founder photos
const founderPhotos = {
  aymar: 'https://customer-assets.emergentagent.com/job_indexvec-ai/artifacts/f9keezek_aymar.webp',
  aniss: 'https://customer-assets.emergentagent.com/job_glassmorphic-7/artifacts/p3cro4gg_Capture%20d%E2%80%99e%CC%81cran%202026-01-03%20a%CC%80%2016.06.03.png',
};

const ChangeManagement = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % changeManagement.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  const colors = ['#3dd3ee', '#fad24b', '#c8aff0', '#b4dc19'];

  const handleContactClick = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={sectionRef} className="relative py-24 bg-[#f8f8f8] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#c8aff0]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-[#3dd3ee]/10 rounded-full blur-[120px]" />
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Manifeste Section */}
        <div
          className={`mb-20 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="relative bg-[#0a0a0b] rounded-3xl p-8 md:p-12 overflow-hidden">
            {/* Background effects */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#3dd3ee]/10 rounded-full blur-[100px]" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#fad24b]/10 rounded-full blur-[100px]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#c8aff0]/5 rounded-full blur-[80px]" />
            
            <div className="relative">
              {/* Header with photos */}
              <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
                <div>
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-xs font-medium text-white/70 mb-4">
                    <Quote className="w-3 h-3 text-[#fad24b]" />
                    Manifeste
                  </span>
                  <h3 className="text-3xl md:text-4xl font-bold text-white">
                    Notre <span className="text-[#3dd3ee]">philosophie</span>
                  </h3>
                </div>
                
                {/* Founders photos */}
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-4">
                    <div 
                      className="w-16 h-16 rounded-full border-3 border-[#0a0a0b] bg-cover bg-center ring-2 ring-[#3dd3ee]/50"
                      style={{ backgroundImage: `url(${founderPhotos.aymar})` }}
                    />
                    <div 
                      className="w-16 h-16 rounded-full border-3 border-[#0a0a0b] bg-cover bg-center ring-2 ring-[#fad24b]/50 grayscale"
                      style={{ backgroundImage: `url(${founderPhotos.aniss})`, backgroundPosition: 'center 30%' }}
                    />
                  </div>
                  <div className="text-sm">
                    <div className="text-white font-medium">Aymar & Aniss</div>
                    <div className="text-white/40 text-xs">Co-fondateurs</div>
                  </div>
                </div>
              </div>

              {/* Manifeste content */}
              <div className="space-y-4 text-white/80 leading-relaxed">
                <p>
                  Nous ne faisons pas de l'IA pour faire de l'IA. Nous faisons de l'IA{' '}
                  <strong className="text-white">parce que notre métier y a été contraint</strong>.
                </p>
                
                <p>
                  Depuis <strong className="text-[#3dd3ee]">2019</strong>, nous construisons des systèmes IA là où les données sont complexes, 
                  les sources critiques et les erreurs coûteuses. Avant les démos, avant les POC, avant les effets d'annonce.
                </p>
                
                <p className="text-lg">
                  Nous avons appris une chose essentielle :{' '}
                  <strong className="text-[#fad24b]">le modèle ne fait pas la valeur. La donnée, le contexte et le workflow la font.</strong>
                </p>
                
                <p>
                  Nous avons confié aux IA ce qui devait l'être. Nous avons gardé l'humain là où l'IA échoue encore.
                </p>
                
                <div className="flex flex-wrap gap-3 py-4">
                  <span className="px-3 py-1.5 bg-[#3dd3ee]/20 text-[#3dd3ee] rounded-lg text-sm font-medium">RAG métier</span>
                  <span className="px-3 py-1.5 bg-[#b4dc19]/20 text-[#b4dc19] rounded-lg text-sm font-medium">Workflows repensés</span>
                  <span className="px-3 py-1.5 bg-[#c8aff0]/20 text-[#c8aff0] rounded-lg text-sm font-medium">Assistants adoptés</span>
                </div>
                
                <p className="text-white font-medium">
                  Pas des promesses. <span className="text-[#b4dc19]">Des systèmes en production.</span>
                </p>
                
                <p>
                  Aujourd'hui, nous déployons chez nos clients{' '}
                  <strong className="text-white">ce que nous avons d'abord déployé chez nous</strong> : 
                  des IA intégrées, mesurables, adoptées.
                </p>
                
                <div className="pt-4 border-t border-white/10 mt-6">
                  <p className="text-white/60 mb-4">
                    Si vous cherchez une démo, ce n'est pas ici.
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    <p className="text-white font-semibold">
                      Si vous cherchez une IA qui fonctionne,
                    </p>
                    <button
                      onClick={handleContactClick}
                      className="group inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#3dd3ee] to-[#c8aff0] rounded-xl text-[#0a0a0b] font-semibold text-sm transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(61,211,238,0.4)]"
                    >
                      Commençons
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-black/5 text-sm font-medium text-[#61525a] mb-6 shadow-sm">
              <Sparkles className="w-4 h-4 text-[#fad24b]" />
              Différenciation clé
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0a0a0b] mb-6">
              Accompagnement au{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #ff8c19, #fad24b)' }}>
                changement
              </span>
            </h2>
            <p className="text-xl text-[#61525a] mb-8 leading-relaxed">
              Une solution IA peut être excellente techniquement... et échouer si elle n'est pas adoptée.
              Nous intégrons un accompagnement structuré basé sur{' '}
              <strong className="text-[#0a0a0b]">plus de 10 ans d'expérience</strong>.
            </p>

            {/* Result Card */}
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-black/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#b4dc19]/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#b4dc19]" />
                </div>
                <span className="font-semibold text-[#0a0a0b]">Résultat attendu</span>
              </div>
              <p className="text-[#61525a]">
                Un outil <span className="text-[#0a0a0b] font-medium">utilisé</span>, qui s'inscrit dans les routines de travail,
                et dont la performance progresse avec le temps.
              </p>
            </div>
          </div>

          {/* Right - Interactive Steps */}
          <div
            className={`transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="space-y-4">
              {changeManagement.map((step, index) => {
                const Icon = iconMap[step.icon];
                const isActive = activeStep === index;
                const color = colors[index];

                return (
                  <div
                    key={step.title}
                    className={`relative p-6 rounded-2xl cursor-pointer transition-all duration-500 ${
                      isActive
                        ? 'bg-white shadow-xl scale-[1.02]'
                        : 'bg-white/50 hover:bg-white hover:shadow-lg'
                    }`}
                    onClick={() => setActiveStep(index)}
                  >
                    {/* Progress Line */}
                    <div
                      className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl transition-all duration-500"
                      style={{ backgroundColor: isActive ? color : 'transparent' }}
                    />

                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div
                        className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500`}
                        style={{ backgroundColor: isActive ? color : '#f8f8f8' }}
                      >
                        <Icon
                          className={`w-6 h-6 transition-colors duration-500`}
                          style={{ color: isActive ? 'white' : '#61525a' }}
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <h3
                          className={`text-lg font-bold mb-2 transition-colors duration-500 ${
                            isActive ? 'text-[#0a0a0b]' : 'text-[#61525a]'
                          }`}
                        >
                          {step.title}
                        </h3>
                        <p
                          className={`text-sm transition-all duration-500 overflow-hidden ${
                            isActive ? 'text-[#61525a] max-h-20 opacity-100' : 'max-h-0 opacity-0'
                          }`}
                        >
                          {step.description}
                        </p>
                      </div>

                      {/* Step Number */}
                      <div
                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500`}
                        style={{
                          backgroundColor: isActive ? color : '#f8f8f8',
                          color: isActive ? 'white' : '#61525a',
                        }}
                      >
                        {index + 1}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Progress Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {changeManagement.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveStep(index)}
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: activeStep === index ? '32px' : '8px',
                    backgroundColor: activeStep === index ? colors[index] : '#e0e0e0',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChangeManagement;
